"use client";

import styles from "@/styles/Header.module.scss";
import { useEffect, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import BurgerIcon from "@/app/svg/BurgerIcon";
import CloseIcon from "@/app/svg/CloseIcon";
import { INTERNAL_LINK, PageType, SocialAccounts } from "@/types";
import { usePathname } from "next/navigation";
import { Logo } from "./Logo";
import { SocialLink } from "./SocialLink";
import { WeglotLangSwitch } from "../WeglotLangSwitch";

export default function Header({ pages, socialAccounts, weglotID }: { pages: PageType[] | null; socialAccounts: SocialAccounts | null; weglotID: string | null }) {
	const [navOpen, setNavOpen] = useState<boolean>(false);
	const [targetPage, setTargetPage] = useState<string>("page");
	const pathname = usePathname();

	const [navLinks, setNavLinks] = useState<{ name: string; slug: string }[]>(
		pages
			? pages
					.filter((p) => !p.isHouse)
					.map((page) => {
						return {
							name: page.title,
							slug: page.slug,
						};
					})
			: []
	);

	const [navLinksHouse, setNavLinksHouse] = useState<{ name: string; slug: string }[]>(
		pages
			? pages
					.filter((p) => p.isHouse)
					.map((page) => {
						return {
							name: page.title,
							slug: page.slug,
						};
					})
			: []
	);

	useEffect(() => {
		if (pathname?.startsWith("/preview") && targetPage !== "preview") {
			setTargetPage("preview");
		}
	}, [targetPage, pathname]);

	const ToggleNav = () => {
		setNavOpen(!navOpen);

		document.dispatchEvent(new Event("navigationToggled"));
	};

	return (
		<>
			<header className={`${styles.header} ${styles.fixed} ${navOpen ? styles.navOpen : ""}`}>
				<div className={`max-wrapper-content flex flex-between flex-align-v-center`}>
					<div className={styles.logo}>
						<Link aria-label="Link zur Startseite" href="/" onClick={() => setNavOpen(false)}>
							<Logo fill={navOpen ? "#fff" : "#000"} />
						</Link>
					</div>
					<div className="sub-component flex flex-v-center">
						<WeglotLangSwitch weglotID={weglotID} />
						<button title="Menu öffnen" className={`icon-button ${styles.burger}`} onClick={ToggleNav}>
							{!navOpen && <BurgerIcon fill="var(--header-burger-color)" />}
							{navOpen && <CloseIcon fill="#fff" />}
						</button>
					</div>
				</div>
			</header>

			<AnimatePresence>
				{navOpen && (
					<motion.nav className={styles.nav} initial={{ x: "100%" }} animate={{ x: "0" }} exit={{ x: "100%" }} transition={{ duration: 0.25 }}>
						<div className={`max-wrapper-content ${styles.desktopFlex} ${styles.verticalLine}`}>
							<section className={`flex flex-column align-right-desktop flex-1 flex-v-center max-height ${styles.center} ${styles.mobilePadding}`}>
								<div className={styles.linkBlock}>
									<Link className={styles.default} href={INTERNAL_LINK.Startseite} onClick={() => setNavOpen(false)}>
										Startseite
									</Link>
								</div>

								{/** Nur für mobile */}
								<div className="mobile-only">
									<div className={`${styles.linkBlockEmpty}`}></div>

									<div className={styles.navLinkWrapper}>
										{navLinksHouse.map((link) => {
											return (
												<div className={styles.linkBlock} key={link.slug}>
													<Link className={styles.default} href={`${INTERNAL_LINK.Startseite}exhibition/${link.slug}`} onClick={() => setNavOpen(false)}>
														{link.name}
													</Link>
												</div>
											);
										})}
									</div>
									<div className={`${styles.linkBlockEmpty}`}></div>
									<div className={styles.navLinkWrapper}>
										{navLinks.map((link) => {
											return (
												<div className={styles.linkBlock} key={link.slug}>
													<Link className={styles.default} href={`${INTERNAL_LINK.Startseite}exhibition/${link.slug}`} onClick={() => setNavOpen(false)}>
														{link.name}
													</Link>
												</div>
											);
										})}
									</div>

									<div className={`${styles.linkBlockEmpty}`}></div>
								</div>

								<div className={styles.linkBlock + " translate"}>
									<Link className={styles.default} href={INTERNAL_LINK.About} onClick={() => setNavOpen(false)}>
										Über die BUA
									</Link>
								</div>
								<div className={styles.linkBlock + " translate"}>
									<Link className={styles.default} href={INTERNAL_LINK.BARN} onClick={() => setNavOpen(false)}>
										THE BARN
									</Link>
								</div>
								<div className={styles.linkBlock + " translate"}>
									<Link className={styles.default} href={INTERNAL_LINK.Impressum} onClick={() => setNavOpen(false)}>
										Impressum
									</Link>
								</div>
								<div className={styles.linkBlock + " translate"}>
									<Link className={styles.default} href={INTERNAL_LINK.Datenschutz} onClick={() => setNavOpen(false)}>
										Datenschutz
									</Link>
								</div>

								{socialAccounts && (
									<div className="header-social-links">
										<SocialLink socialNetwork="LinkedIn" url={socialAccounts.linkedin} />
										<SocialLink socialNetwork="Instagram" url={socialAccounts.instagram} />
										<SocialLink socialNetwork="Bluesky" url={socialAccounts.bluesky} />
									</div>
								)}
							</section>

							{/** Nur auf Desktop sichtbar */}
							<section className={`desktop-only-flex flex flex-column flex-1 flex-v-center max-height ` + styles.center}>
								<div className={styles.navLinkWrapper}>
									{navLinksHouse.map((link) => {
										return (
											<div className={styles.linkBlock} key={link.slug}>
												<Link className={styles.default} href={`${INTERNAL_LINK.Startseite}exhibition/${link.slug}`} onClick={() => setNavOpen(false)}>
													{link.name}
												</Link>
											</div>
										);
									})}
								</div>

								<div className={styles.navLinkWrapper}>
									{navLinks.map((link) => {
										return (
											<div className={styles.linkBlock} key={link.slug}>
												<Link className={styles.default} href={`${INTERNAL_LINK.Startseite}exhibition/${link.slug}`} onClick={() => setNavOpen(false)}>
													{link.name}
												</Link>
											</div>
										);
									})}
								</div>
							</section>
						</div>
					</motion.nav>
				)}
			</AnimatePresence>
		</>
	);
}
